import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import { useEffect, useState } from "react";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import { CgBox } from "react-icons/cg";
import { CiWheat } from "react-icons/ci";
import { FaScaleUnbalancedFlip } from "react-icons/fa6";
import { GrDocumentPdf, GrMoney } from "react-icons/gr";
import {
  ImParagraphRight,
  ImPrinter,
  ImSpinner11,
  ImWarning,
} from "react-icons/im";
import { PiCowDuotone } from "react-icons/pi";
import { TbTargetArrow } from "react-icons/tb";
import { VscGraph } from "react-icons/vsc";
import BotaoRelatorio from "../../../components/relatorios/BotaoRelatorio";
import CabecalhoRelatorio from "../../../components/relatorios/CabecalhoRelatorio";
import { api } from "../../../services/api";
import { exportarRelatorioPDF, formatarPeriodoFiltro } from "../../../utils/relatorioPdf";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  Filler,
);

// ─── Modal de detalhes do KPI ────────────────────────────────────────────────
function KpiModal({ kpi, kpis, onClose, formatarMoeda, formatarNumero, formatarPorcentagem }) {
  if (!kpi) return null;

  const gradients = {
    blue:   "from-blue-500 to-blue-700",
    green:  "from-emerald-500 to-green-700",
    orange: "from-orange-400 to-orange-600",
    purple: "from-purple-500 to-purple-700",
    red:    "from-red-500 to-rose-600",
    teal:   "from-teal-500 to-cyan-700",
  };

  const chartPalettes = {
    blue:   ["#3b82f6","#60a5fa","#93c5fd","#bfdbfe","#dbeafe","#eff6ff"],
    green:  ["#22c55e","#4ade80","#86efac","#bbf7d0","#dcfce7","#f0fdf4"],
    orange: ["#f97316","#fb923c","#fdba74","#fed7aa","#ffedd5","#fff7ed"],
    purple: ["#a855f7","#c084fc","#d8b4fe","#e9d5ff","#f3e8ff","#faf5ff"],
    red:    ["#ef4444","#f87171","#fca5a5","#fecaca","#fee2e2","#fef2f2"],
    teal:   ["#14b8a6","#2dd4bf","#5eead4","#99f6e4","#ccfbf1","#f0fdfa"],
  };

  const insightBg = {
    blue:   "bg-blue-50 text-blue-800",
    green:  "bg-green-50 text-green-800",
    orange: "bg-orange-50 text-orange-800",
    purple: "bg-purple-50 text-purple-800",
    red:    "bg-red-50 text-red-800",
    teal:   "bg-teal-50 text-teal-800",
  };

  const gradient  = gradients[kpi.color]    || gradients.blue;
  const colors    = chartPalettes[kpi.color] || chartPalettes.blue;
  const insightCl = insightBg[kpi.color]    || insightBg.blue;

  const doughnutOpts = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "65%",
    plugins: {
      legend: { position: "bottom", labels: { padding: 14, font: { size: 11 } } },
    },
  };

  const barOpts = (suffix = "") => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: (ctx) => ` ${ctx.raw}${suffix}` } },
    },
    scales: {
      x: { grid: { color: "#f3f4f6" }, ticks: { font: { size: 11 } } },
      y: { grid: { color: "#f3f4f6" }, ticks: { font: { size: 11 } } },
    },
  });

  const lineOpts = (formatLabel) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top", labels: { font: { size: 11 } } },
      tooltip: { callbacks: { label: (ctx) => ` ${formatLabel(ctx.raw)}` } },
    },
    scales: {
      x: { grid: { color: "#f3f4f6" }, ticks: { font: { size: 11 } } },
      y: {
        grid: { color: "#f3f4f6" },
        ticks: { font: { size: 10 }, callback: (v) => `R$${(v / 1000).toFixed(0)}k` },
      },
    },
  });

  const renderContent = () => {
    switch (kpi.id) {

      case "custo_por_hectare": {
        const margens = kpis.agricultura?.margem_por_cultura || [];
        const hasData  = margens.length > 0;
        return {
          title: "Margem por Cultura",
          chart: hasData ? (
            <Bar
              data={{
                labels: margens.map((c) => c.cultura),
                datasets: [{ label: "Margem (%)", data: margens.map((c) => c.margem), backgroundColor: colors.slice(0, margens.length), borderRadius: 6 }],
              }}
              options={barOpts("%")}
            />
          ) : (
            <Doughnut
              data={{
                labels: ["Custo / ha", "Margem estimada"],
                datasets: [{ data: [kpis.agricultura.custo_por_hectare, kpis.agricultura.custo_por_hectare * 0.3], backgroundColor: [colors[0], colors[2]], borderWidth: 3, borderColor: "#fff" }],
              }}
              options={{ ...doughnutOpts, plugins: { ...doughnutOpts.plugins, tooltip: { callbacks: { label: (ctx) => ` ${formatarMoeda(ctx.raw)}` } } } }}
            />
          ),
          insights: [
            { label: "Produtividade Média", value: `${formatarNumero(kpis.agricultura.produtividade_media)} sc/ha` },
            { label: "Custo por Saca",      value: formatarMoeda(kpis.agricultura.custo_por_saca) },
            { label: "Culturas",            value: `${margens.length || "—"}` },
          ],
        };
      }

      case "produtividade_media": {
        const prod    = kpis.graficos?.produtividade_por_cultura || [];
        const hasData = prod.length > 0;
        const margens = kpis.agricultura?.margem_por_cultura || [];
        const margemMedia = margens.length > 0
          ? margens.reduce((acc, c) => acc + c.margem, 0) / margens.length
          : null;
        return {
          title: "Produtividade por Cultura",
          chart: hasData ? (
            <Bar
              data={{
                labels: prod.map((c) => c.cultura),
                datasets: [{ label: "Produtividade (sc/ha)", data: prod.map((c) => c.produtividade), backgroundColor: colors.slice(0, prod.length), borderRadius: 6 }],
              }}
              options={barOpts(" sc/ha")}
            />
          ) : (
            <Doughnut
              data={{
                labels: ["Produtividade Atual", "Meta (60 sc/ha)"],
                datasets: [{ data: [kpis.agricultura.produtividade_media, Math.max(0, 60 - kpis.agricultura.produtividade_media)], backgroundColor: [colors[0], "#e5e7eb"], borderWidth: 3, borderColor: "#fff" }],
              }}
              options={{ ...doughnutOpts, plugins: { ...doughnutOpts.plugins, tooltip: { callbacks: { label: (ctx) => ` ${ctx.raw} sc/ha` } } } }}
            />
          ),
          insights: [
            { label: "Custo / ha",     value: formatarMoeda(kpis.agricultura.custo_por_hectare) },
            { label: "Custo / saca",   value: formatarMoeda(kpis.agricultura.custo_por_saca) },
            { label: "Margem Média",   value: margemMedia !== null ? formatarPorcentagem(margemMedia) : "—" },
          ],
        };
      }

      case "custo_por_saca": {
        const margens = kpis.agricultura?.margem_por_cultura || [];
        const hasData  = margens.length > 0;
        return {
          title: "Margem por Cultura",
          chart: hasData ? (
            <Bar
              data={{
                labels: margens.map((c) => c.cultura),
                datasets: [{ label: "Margem (%)", data: margens.map((c) => c.margem), backgroundColor: colors.slice(0, margens.length), borderRadius: 6 }],
              }}
              options={barOpts("%")}
            />
          ) : (
            <Doughnut
              data={{
                labels: ["Custo por Saca", "Margem estimada"],
                datasets: [{ data: [kpis.agricultura.custo_por_saca, kpis.agricultura.custo_por_saca * 0.4], backgroundColor: [colors[0], colors[2]], borderWidth: 3, borderColor: "#fff" }],
              }}
              options={{ ...doughnutOpts, plugins: { ...doughnutOpts.plugins, tooltip: { callbacks: { label: (ctx) => ` ${formatarMoeda(ctx.raw)}` } } } }}
            />
          ),
          insights: [
            { label: "Custo / ha",      value: formatarMoeda(kpis.agricultura.custo_por_hectare) },
            { label: "Produtividade",   value: `${formatarNumero(kpis.agricultura.produtividade_media)} sc/ha` },
            { label: "Nº de culturas",  value: `${margens.length || "—"}` },
          ],
        };
      }

      case "margem_media": {
        const margens  = kpis.agricultura?.margem_por_cultura || [];
        const hasData   = margens.length > 0;
        const melhor    = hasData ? margens.reduce((a, b) => (a.margem > b.margem ? a : b)) : null;
        const pior      = hasData ? margens.reduce((a, b) => (a.margem < b.margem ? a : b)) : null;
        return {
          title: "Distribuição de Margens",
          chart: hasData ? (
            <Doughnut
              data={{
                labels: margens.map((c) => c.cultura),
                datasets: [{ data: margens.map((c) => Math.abs(c.margem)), backgroundColor: colors.slice(0, margens.length), borderWidth: 3, borderColor: "#fff", hoverOffset: 6 }],
              }}
              options={{ ...doughnutOpts, plugins: { ...doughnutOpts.plugins, tooltip: { callbacks: { label: (ctx) => ` ${ctx.raw}%` } } } }}
            />
          ) : null,
          insights: [
            { label: "Melhor Cultura", value: melhor ? `${melhor.cultura} (${melhor.margem}%)` : "—" },
            { label: "Pior Cultura",   value: pior   ? `${pior.cultura} (${pior.margem}%)`   : "—" },
            { label: "Total Culturas", value: `${margens.length}` },
          ],
        };
      }

      case "gmd": {
        const lotes   = kpis.graficos?.gmd_por_lote || [];
        const hasData  = lotes.length > 0;
        const melhor   = hasData ? lotes.reduce((a, b) => (a.gmd > b.gmd ? a : b)) : null;
        const pior     = hasData ? lotes.reduce((a, b) => (a.gmd < b.gmd ? a : b)) : null;
        return {
          title: "GMD por Lote",
          chart: hasData ? (
            <Bar
              data={{
                labels: lotes.map((l) => l.lote),
                datasets: [{ label: "GMD (kg/dia)", data: lotes.map((l) => l.gmd), backgroundColor: colors.slice(0, lotes.length), borderRadius: 6 }],
              }}
              options={barOpts(" kg/dia")}
            />
          ) : (
            <Doughnut
              data={{
                labels: ["GMD Atual", "Meta (1,2 kg/dia)"],
                datasets: [{ data: [kpis.pecuaria.gmd, Math.max(0, 1.2 - kpis.pecuaria.gmd)], backgroundColor: [colors[0], "#e5e7eb"], borderWidth: 3, borderColor: "#fff" }],
              }}
              options={{ ...doughnutOpts, plugins: { ...doughnutOpts.plugins, tooltip: { callbacks: { label: (ctx) => ` ${ctx.raw} kg/dia` } } } }}
            />
          ),
          insights: [
            { label: "Melhor Lote",  value: melhor ? `${melhor.lote} — ${melhor.gmd} kg` : "—" },
            { label: "Pior Lote",    value: pior   ? `${pior.lote} — ${pior.gmd} kg`     : "—" },
            { label: "Total Lotes",  value: `${lotes.length || "—"}` },
          ],
        };
      }

      case "taxa_mortalidade": {
        const mort    = kpis.pecuaria.taxa_mortalidade || 0;
        const vivos   = Math.max(0, 100 - mort);
        const status  = mort > 5 ? "Alto" : mort > 2 ? "Moderado" : "Normal";
        const stCl    = mort > 5 ? "bg-red-100 text-red-700" : mort > 2 ? "bg-yellow-100 text-yellow-700" : "bg-green-100 text-green-700";
        return {
          title: "Mortalidade vs Sobrevivência",
          chart: (
            <Doughnut
              data={{
                labels: ["Mortalidade", "Sobrevivência"],
                datasets: [{ data: [mort, vivos], backgroundColor: ["#ef4444", "#22c55e"], borderWidth: 3, borderColor: "#fff", hoverOffset: 6 }],
              }}
              options={{ ...doughnutOpts, plugins: { ...doughnutOpts.plugins, tooltip: { callbacks: { label: (ctx) => ` ${ctx.raw.toFixed(1)}%` } } } }}
            />
          ),
          insights: [
            { label: "Mortalidade",   value: formatarPorcentagem(mort),  customClass: stCl },
            { label: "Sobrevivência", value: formatarPorcentagem(vivos) },
            { label: "Status",        value: status, customClass: stCl },
          ],
        };
      }

      case "taxa_prenhez": {
        const prenhez = kpis.pecuaria.taxa_prenhez || 0;
        const nao     = Math.max(0, 100 - prenhez);
        const status  = prenhez >= 85 ? "Excelente" : prenhez >= 70 ? "Bom" : "Abaixo da meta";
        const stCl    = prenhez >= 85 ? "bg-green-100 text-green-700" : prenhez >= 70 ? "bg-yellow-100 text-yellow-700" : "bg-red-100 text-red-700";
        return {
          title: "Taxa de Prenhez do Rebanho",
          chart: (
            <Doughnut
              data={{
                labels: ["Prenhes", "Não Prenhes"],
                datasets: [{ data: [prenhez, nao], backgroundColor: ["#a855f7", "#e5e7eb"], borderWidth: 3, borderColor: "#fff", hoverOffset: 6 }],
              }}
              options={{ ...doughnutOpts, plugins: { ...doughnutOpts.plugins, tooltip: { callbacks: { label: (ctx) => ` ${ctx.raw.toFixed(1)}%` } } } }}
            />
          ),
          insights: [
            { label: "Taxa Prenhez", value: formatarPorcentagem(prenhez) },
            { label: "Meta",         value: "85%" },
            { label: "Status",       value: status, customClass: stCl },
          ],
        };
      }

      case "lotacao": {
        const lotes   = kpis.graficos?.gmd_por_lote || [];
        const lotacao = kpis.pecuaria.lotacao || 0;
        const hasData  = lotes.length > 0;
        const ocup    = ((lotacao / 2) * 100).toFixed(0);
        return {
          title: "Lotação vs Capacidade",
          chart: hasData ? (
            <Bar
              data={{
                labels: lotes.map((l) => l.lote),
                datasets: [{ label: "GMD (kg/dia)", data: lotes.map((l) => l.gmd), backgroundColor: colors.slice(0, lotes.length), borderRadius: 6 }],
              }}
              options={barOpts(" kg/dia")}
            />
          ) : (
            <Doughnut
              data={{
                labels: ["Lotação Atual", "Capacidade disponível"],
                datasets: [{ data: [lotacao, Math.max(0, 2 - lotacao)], backgroundColor: [colors[0], "#e5e7eb"], borderWidth: 3, borderColor: "#fff" }],
              }}
              options={{ ...doughnutOpts, plugins: { ...doughnutOpts.plugins, tooltip: { callbacks: { label: (ctx) => ` ${ctx.raw.toFixed(2)} UA/ha` } } } }}
            />
          ),
          insights: [
            { label: "Lotação Atual",   value: `${formatarNumero(lotacao)} UA/ha` },
            { label: "Capacidade Máx.", value: "2,0 UA/ha" },
            { label: "Ocupação",        value: `${ocup}%` },
          ],
        };
      }

      case "lucro_liquido": {
        const evolucao = kpis.graficos?.evolucao_financeira || [];
        const hasData   = evolucao.length > 0;
        return {
          title: "Evolução Financeira",
          chart: hasData ? (
            <Line
              data={{
                labels: evolucao.map((e) => e.mes),
                datasets: [
                  { label: "Receita",  data: evolucao.map((e) => e.receita),  borderColor: "#22c55e", backgroundColor: "rgba(34,197,94,0.1)",  fill: true, tension: 0.4, pointRadius: 4 },
                  { label: "Despesa",  data: evolucao.map((e) => e.despesa),  borderColor: "#ef4444", backgroundColor: "rgba(239,68,68,0.1)",  fill: true, tension: 0.4, pointRadius: 4 },
                ],
              }}
              options={lineOpts(formatarMoeda)}
            />
          ) : (
            <Doughnut
              data={{
                labels: ["Lucro Líquido", "Custo Total"],
                datasets: [{ data: [kpis.financeiro.lucro_liquido, kpis.financeiro.ponto_equilibrio || kpis.financeiro.lucro_liquido * 2], backgroundColor: [colors[0], "#e5e7eb"], borderWidth: 3, borderColor: "#fff" }],
              }}
              options={{ ...doughnutOpts, plugins: { ...doughnutOpts.plugins, tooltip: { callbacks: { label: (ctx) => ` ${formatarMoeda(ctx.raw)}` } } } }}
            />
          ),
          insights: [
            { label: "ROI",                value: formatarPorcentagem(kpis.financeiro.roi) },
            { label: "Ponto de Equilíbrio",value: formatarMoeda(kpis.financeiro.ponto_equilibrio) },
            { label: "Meses analisados",   value: `${evolucao.length || 0}` },
          ],
        };
      }

      case "roi": {
        const evolucao = kpis.graficos?.evolucao_financeira || [];
        const roi       = kpis.financeiro.roi || 0;
        const hasData   = evolucao.length > 0;
        return {
          title: "Receita vs Despesa por Mês",
          chart: hasData ? (
            <Bar
              data={{
                labels: evolucao.map((e) => e.mes),
                datasets: [
                  { label: "Receita", data: evolucao.map((e) => e.receita), backgroundColor: "#22c55e", borderRadius: 4 },
                  { label: "Despesa", data: evolucao.map((e) => e.despesa), backgroundColor: "#ef4444", borderRadius: 4 },
                ],
              }}
              options={{
                responsive: true, maintainAspectRatio: false,
                plugins: { legend: { position: "top" }, tooltip: { callbacks: { label: (ctx) => ` ${formatarMoeda(ctx.raw)}` } } },
                scales: { x: { grid: { color: "#f3f4f6" } }, y: { grid: { color: "#f3f4f6" }, ticks: { callback: (v) => `R$${(v / 1000).toFixed(0)}k`, font: { size: 10 } } } },
              }}
            />
          ) : (
            <Doughnut
              data={{
                labels: ["Retorno (%)", "Restante (%)"],
                datasets: [{ data: [roi, Math.max(0, 100 - roi)], backgroundColor: [colors[0], "#e5e7eb"], borderWidth: 3, borderColor: "#fff" }],
              }}
              options={{ ...doughnutOpts, plugins: { ...doughnutOpts.plugins, tooltip: { callbacks: { label: (ctx) => ` ${ctx.raw.toFixed(1)}%` } } } }}
            />
          ),
          insights: [
            { label: "ROI",                 value: formatarPorcentagem(roi) },
            { label: "Lucro Líquido",       value: formatarMoeda(kpis.financeiro.lucro_liquido) },
            { label: "Ponto de Equilíbrio", value: formatarMoeda(kpis.financeiro.ponto_equilibrio) },
          ],
        };
      }

      case "ponto_equilibrio": {
        const evolucao = kpis.graficos?.evolucao_financeira || [];
        const pe        = kpis.financeiro.ponto_equilibrio || 0;
        const hasData   = evolucao.length > 0;
        return {
          title: "Receita vs Ponto de Equilíbrio",
          chart: hasData ? (
            <Line
              data={{
                labels: evolucao.map((e) => e.mes),
                datasets: [
                  { label: "Receita", data: evolucao.map((e) => e.receita), borderColor: "#22c55e", backgroundColor: "rgba(34,197,94,0.1)", fill: true, tension: 0.4, pointRadius: 4 },
                  { label: "Ponto de Equilíbrio", data: evolucao.map(() => pe), borderColor: "#f97316", borderDash: [6, 4], borderWidth: 2, pointRadius: 0, fill: false },
                ],
              }}
              options={lineOpts(formatarMoeda)}
            />
          ) : (
            <Doughnut
              data={{
                labels: ["Ponto de Equilíbrio", "Margem acima"],
                datasets: [{ data: [pe, Math.max(0, kpis.financeiro.lucro_liquido)], backgroundColor: [colors[0], "#22c55e"], borderWidth: 3, borderColor: "#fff" }],
              }}
              options={{ ...doughnutOpts, plugins: { ...doughnutOpts.plugins, tooltip: { callbacks: { label: (ctx) => ` ${formatarMoeda(ctx.raw)}` } } } }}
            />
          ),
          insights: [
            { label: "Ponto de Equilíbrio", value: formatarMoeda(pe) },
            { label: "Lucro Líquido",       value: formatarMoeda(kpis.financeiro.lucro_liquido) },
            { label: "ROI",                 value: formatarPorcentagem(kpis.financeiro.roi) },
          ],
        };
      }

      default:
        return { title: "", chart: null, insights: [] };
    }
  };

  const { title: chartTitle, chart, insights } = renderContent();

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header com gradiente */}
        <div className={`bg-gradient-to-br ${gradient} p-6 rounded-t-2xl text-white`}>
          <div className="flex justify-between items-start">
            <div>
              <p className="text-white/60 text-xs uppercase tracking-widest font-semibold">{kpi.section}</p>
              <h3 className="text-xl font-bold mt-1">{kpi.title}</h3>
              <p className="text-4xl font-black mt-2 tracking-tight">{kpi.value}</p>
              {kpi.subtitle && (
                <p className="text-white/70 text-sm mt-1">{kpi.subtitle}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="bg-white/20 hover:bg-white/30 rounded-full p-2 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Corpo */}
        <div className="p-6">
          {chartTitle && (
            <p className="text-sm font-semibold text-gray-600 mb-3">{chartTitle}</p>
          )}
          <div className="h-72 mb-6">
            {chart ?? (
              <div className="h-full flex items-center justify-center text-gray-300 text-sm">
                Sem dados disponíveis para o período selecionado
              </div>
            )}
          </div>

          {insights.length > 0 && (
            <div className="grid grid-cols-3 gap-3">
              {insights.map((ins, i) => (
                <div
                  key={i}
                  className={`rounded-xl px-4 py-3 text-center ${ins.customClass || insightCl}`}
                >
                  <p className="text-xs font-semibold uppercase tracking-wide opacity-70">{ins.label}</p>
                  <p className="text-sm font-bold mt-1">{ins.value}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────
const DashboardKPI = () => {
  const [kpis, setKpis] = useState({
    agricultura: {
      custo_por_hectare: 0,
      produtividade_media: 0,
      custo_por_saca: 0,
      margem_por_cultura: [],
      ultima_atualizacao: "",
    },
    pecuaria: {
      gmd: 0,
      taxa_mortalidade: 0,
      taxa_prenhez: 0,
      lotacao: 0,
      ultima_atualizacao: "",
    },
    financeiro: {
      lucro_liquido: 0,
      roi: 0,
      ponto_equilibrio: 0,
      ultima_atualizacao: "",
    },
    graficos: {
      produtividade_por_cultura: [],
      margem_por_cultura: [],
      gmd_por_lote: [],
      evolucao_financeira: [],
    },
  });

  const [loading, setLoading] = useState(false);
  const [selectedKpi, setSelectedKpi] = useState(null);
  const [periodo, setPeriodo] = useState({
    inicio: new Date(new Date().getFullYear(), 0, 1).toISOString().split("T")[0],
    fim: new Date().toISOString().split("T")[0],
  });

  const [animatedValues, setAnimatedValues] = useState({
    custo_por_hectare: 0,
    produtividade_media: 0,
    gmd: 0,
    lucro_liquido: 0,
    roi: 0,
  });

  useEffect(() => { carregarKPIs(); }, [periodo]);
  useEffect(() => { animarValores(); }, [kpis]);

  const animarValores = () => {
    const duracao = 1000;
    const steps = 60;
    let step = 0;

    const valoresFinais = {
      custo_por_hectare: kpis.agricultura.custo_por_hectare,
      produtividade_media: kpis.agricultura.produtividade_media,
      gmd: kpis.pecuaria.gmd,
      lucro_liquido: kpis.financeiro.lucro_liquido,
      roi: kpis.financeiro.roi,
    };

    const interval = setInterval(() => {
      step++;
      const progress = step / steps;
      setAnimatedValues({
        custo_por_hectare: valoresFinais.custo_por_hectare * progress,
        produtividade_media: valoresFinais.produtividade_media * progress,
        gmd: valoresFinais.gmd * progress,
        lucro_liquido: valoresFinais.lucro_liquido * progress,
        roi: valoresFinais.roi * progress,
      });
      if (step >= steps) clearInterval(interval);
    }, duracao / steps);
  };

  const carregarKPIs = async () => {
    setLoading(true);
    try {
      const response = await api.get("/relatorios/dashboard/kpis/", {
        params: { data_inicio: periodo.inicio, data_fim: periodo.fim },
      });
      setKpis(response.data);
    } catch (error) {
      console.error("Erro ao carregar KPIs:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatarMoeda = (valor) => {
    if (!valor && valor !== 0) return "R$ 0,00";
    return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(valor);
  };

  const formatarNumero = (valor, casas = 2) => {
    if (!valor && valor !== 0) return "0";
    return valor.toFixed(casas);
  };

  const formatarPorcentagem = (valor) => {
    if (!valor && valor !== 0) return "0%";
    return `${valor.toFixed(1)}%`;
  };

  // ─── KpiCard clicável ────────────────────────────────────────────────────
  const KpiCard = ({ title, value, subtitle, icon, color = "blue", onClick }) => {
    const colors = {
      blue:   "bg-blue-500",
      green:  "bg-green-500",
      purple: "bg-purple-500",
      orange: "bg-orange-500",
      red:    "bg-red-500",
      teal:   "bg-teal-500",
    };

    const rings = {
      blue:   "hover:ring-blue-300",
      green:  "hover:ring-green-300",
      purple: "hover:ring-purple-300",
      orange: "hover:ring-orange-300",
      red:    "hover:ring-red-300",
      teal:   "hover:ring-teal-300",
    };

    return (
      <div
        className={`bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-lg transition-all cursor-pointer hover:scale-[1.03] select-none hover:ring-2 ${rings[color]}`}
        onClick={onClick}
        title="Clique para ver detalhes"
      >
        <div className="flex items-start justify-between mb-3">
          <div className={`p-2 rounded-lg ${colors[color]} bg-opacity-10`}>
            <span className="text-xl">{icon}</span>
          </div>
          <span className="text-gray-300 text-xs">Ver gráfico →</span>
        </div>
        <div className="mb-1">
          <span className="text-2xl font-bold text-gray-800">{value}</span>
          {subtitle && <span className="text-sm text-gray-500 ml-1">{subtitle}</span>}
        </div>
        <div className="text-sm text-gray-500">{title}</div>
      </div>
    );
  };

  // ─── Exportar PDF ────────────────────────────────────────────────────────
  // Este dashboard não tem abas (as 3 seções ficam visíveis ao mesmo tempo),
  // diferente de Rentabilidade/Conformidade. Por isso as três tabelas
  // originais (Agricultura, Pecuária, Financeiro) viram uma única tabela
  // achatada com coluna "Categoria", exportada numa só chamada.
  const exportarPDF = async () => {
    const colunas = [
      { key: "categoria", label: "Categoria" },
      { key: "indicador", label: "Indicador" },
      { key: "valor", label: "Valor" },
    ];

    const dadosPdf = [
      { categoria: "Agricultura", indicador: "Custo por Hectare", valor: formatarMoeda(kpis.agricultura.custo_por_hectare) },
      { categoria: "Agricultura", indicador: "Produtividade Média", valor: `${formatarNumero(kpis.agricultura.produtividade_media)} saca/ha` },
      { categoria: "Agricultura", indicador: "Custo por Saca", valor: formatarMoeda(kpis.agricultura.custo_por_saca) },
      { categoria: "Pecuária", indicador: "GMD (Ganho Médio Diário)", valor: `${formatarNumero(kpis.pecuaria.gmd, 3)} kg/dia` },
      { categoria: "Pecuária", indicador: "Taxa de Mortalidade", valor: formatarPorcentagem(kpis.pecuaria.taxa_mortalidade) },
      { categoria: "Pecuária", indicador: "Taxa de Prenhez", valor: formatarPorcentagem(kpis.pecuaria.taxa_prenhez) },
      { categoria: "Pecuária", indicador: "Lotação", valor: `${formatarNumero(kpis.pecuaria.lotacao)} UA/ha` },
      { categoria: "Financeiro", indicador: "Lucro Líquido", valor: formatarMoeda(kpis.financeiro.lucro_liquido) },
      { categoria: "Financeiro", indicador: "ROI (Retorno sobre Investimento)", valor: formatarPorcentagem(kpis.financeiro.roi) },
      { categoria: "Financeiro", indicador: "Ponto de Equilíbrio", valor: formatarMoeda(kpis.financeiro.ponto_equilibrio) },
    ];

    await exportarRelatorioPDF({
      titulo: "Dashboard de KPIs",
      colunas,
      dados: dadosPdf,
      periodo: formatarPeriodoFiltro(periodo.inicio, periodo.fim),
      columnStyles: { 0: { cellWidth: 40 } },
    });
  };

  const imprimir = () => {
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>Dashboard de KPIs</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #2c3e50; font-size: 24px; }
            h2 { color: #3498db; font-size: 18px; margin-top: 25px; }
            .periodo { color: #7f8c8d; margin-bottom: 20px; }
            .grid { display: flex; flex-wrap: wrap; gap: 15px; margin-bottom: 25px; }
            .card { background: #f8f9fa; padding: 15px; border-radius: 8px; min-width: 180px; }
            .card .valor { font-size: 24px; font-weight: bold; color: #2c3e50; }
            .card .label { font-size: 12px; color: #7f8c8d; margin-top: 5px; }
            .footer { margin-top: 30px; font-size: 10px; color: #7f8c8d; text-align: center; }
          </style>
        </head>
        <body>
          <h1>Dashboard de KPIs</h1>
          <div class="periodo">Período: ${periodo.inicio} a ${periodo.fim}</div>
          <h2>🌾 Agricultura</h2>
          <div class="grid">
            <div class="card"><div class="valor">${formatarMoeda(kpis.agricultura.custo_por_hectare)}</div><div class="label">Custo por Hectare</div></div>
            <div class="card"><div class="valor">${formatarNumero(kpis.agricultura.produtividade_media)} saca/ha</div><div class="label">Produtividade Média</div></div>
            <div class="card"><div class="valor">${formatarMoeda(kpis.agricultura.custo_por_saca)}</div><div class="label">Custo por Saca</div></div>
          </div>
          <h2>🐄 Pecuária</h2>
          <div class="grid">
            <div class="card"><div class="valor">${formatarNumero(kpis.pecuaria.gmd, 3)} kg/dia</div><div class="label">GMD</div></div>
            <div class="card"><div class="valor">${formatarPorcentagem(kpis.pecuaria.taxa_mortalidade)}</div><div class="label">Taxa de Mortalidade</div></div>
            <div class="card"><div class="valor">${formatarPorcentagem(kpis.pecuaria.taxa_prenhez)}</div><div class="label">Taxa de Prenhez</div></div>
            <div class="card"><div class="valor">${formatarNumero(kpis.pecuaria.lotacao)} UA/ha</div><div class="label">Lotação</div></div>
          </div>
          <h2>💰 Financeiro</h2>
          <div class="grid">
            <div class="card"><div class="valor">${formatarMoeda(kpis.financeiro.lucro_liquido)}</div><div class="label">Lucro Líquido</div></div>
            <div class="card"><div class="valor">${formatarPorcentagem(kpis.financeiro.roi)}</div><div class="label">ROI</div></div>
            <div class="card"><div class="valor">${formatarMoeda(kpis.financeiro.ponto_equilibrio)}</div><div class="label">Ponto de Equilíbrio</div></div>
          </div>
          <div class="footer">Sistema de Gestão Agropecuária • Relatório Gerado Automaticamente</div>
          <script>window.onload = () => { window.print(); setTimeout(() => window.close(), 500); }<\/script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // ─── Dados dos gráficos existentes (usados fora do modal) ────────────────
  const margemChartData = {
    labels: kpis.agricultura.margem_por_cultura.map((c) => c.cultura),
    datasets: [{
      label: "Margem (%)",
      data: kpis.agricultura.margem_por_cultura.map((c) => c.margem),
      backgroundColor: "rgba(54, 162, 235, 0.5)",
      borderColor: "rgba(54, 162, 235, 1)",
      borderWidth: 1,
    }],
  };

  const gmdChartData = {
    labels: kpis.graficos?.gmd_por_lote?.map((l) => l.lote) || [],
    datasets: [{
      label: "GMD (kg/dia)",
      data: kpis.graficos?.gmd_por_lote?.map((l) => l.gmd) || [],
      backgroundColor: "rgba(75, 192, 192, 0.5)",
      borderColor: "rgba(75, 192, 192, 1)",
      borderWidth: 1,
    }],
  };

  const evolucaoChartData = {
    labels: kpis.graficos?.evolucao_financeira?.map((e) => e.mes) || [],
    datasets: [
      { label: "Receita", data: kpis.graficos?.evolucao_financeira?.map((e) => e.receita) || [], borderColor: "rgba(46, 204, 113, 1)", backgroundColor: "rgba(46, 204, 113, 0.1)", fill: true, tension: 0.4 },
      { label: "Despesa", data: kpis.graficos?.evolucao_financeira?.map((e) => e.despesa) || [], borderColor: "rgba(231, 76, 60, 1)",  backgroundColor: "rgba(231, 76, 60, 0.1)",  fill: true, tension: 0.4 },
    ],
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <CabecalhoRelatorio
          icone={ImParagraphRight}
          titulo="Dashboard de KPIs"
          descricao="Indicadores-chave de desempenho da fazenda"
          acoes={
            <>
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={periodo.inicio}
                  onChange={(e) => setPeriodo({ ...periodo, inicio: e.target.value })}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-green focus:border-transparent outline-none transition"
                />
                <span className="text-gray-500 text-sm">até</span>
                <input
                  type="date"
                  value={periodo.fim}
                  onChange={(e) => setPeriodo({ ...periodo, fim: e.target.value })}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-green focus:border-transparent outline-none transition"
                />
              </div>
              <BotaoRelatorio
                variante="secundario"
                onClick={carregarKPIs}
                carregando={loading}
                title="Atualizar"
              >
                <ImSpinner11 size={18} />
              </BotaoRelatorio>
              <BotaoRelatorio variante="pdf" onClick={exportarPDF} title="Exportar PDF">
                <GrDocumentPdf size={18} />
              </BotaoRelatorio>
              <BotaoRelatorio variante="imprimir" onClick={imprimir} title="Imprimir">
                <ImPrinter size={18} />
              </BotaoRelatorio>
            </>
          }
        />

        {/* Seção Agricultura */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <CiWheat size={24} /> Agricultura
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <KpiCard
              title="Custo por Hectare"
              value={formatarMoeda(animatedValues.custo_por_hectare)}
              subtitle="/ha"
              icon={<GrMoney size={20} />}
              color="blue"
              onClick={() => setSelectedKpi({ id: "custo_por_hectare", title: "Custo por Hectare", value: formatarMoeda(kpis.agricultura.custo_por_hectare), subtitle: "por hectare", section: "Agricultura", color: "blue" })}
            />
            <KpiCard
              title="Produtividade Média"
              value={formatarNumero(animatedValues.produtividade_media)}
              subtitle="saca/ha"
              icon={<CgBox size={20} />}
              color="green"
              onClick={() => setSelectedKpi({ id: "produtividade_media", title: "Produtividade Média", value: `${formatarNumero(kpis.agricultura.produtividade_media)} sc/ha`, section: "Agricultura", color: "green" })}
            />
            <KpiCard
              title="Custo por Saca"
              value={formatarMoeda(kpis.agricultura.custo_por_saca)}
              subtitle="/saca"
              icon={<GrMoney size={20} />}
              color="orange"
              onClick={() => setSelectedKpi({ id: "custo_por_saca", title: "Custo por Saca", value: formatarMoeda(kpis.agricultura.custo_por_saca), subtitle: "por saca", section: "Agricultura", color: "orange" })}
            />
            <KpiCard
              title="Margem Média"
              value={formatarPorcentagem(28.5)}
              icon={<TbTargetArrow size={20} />}
              color="purple"
              onClick={() => setSelectedKpi({ id: "margem_media", title: "Margem Média", value: formatarPorcentagem(28.5), section: "Agricultura", color: "purple" })}
            />
          </div>

          {kpis.agricultura.margem_por_cultura.length > 0 && (
            <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Margem por Cultura</h3>
              <div className="h-80">
                <Bar data={margemChartData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: "top" }, tooltip: { callbacks: { label: (ctx) => `${ctx.raw}%` } } } }} />
              </div>
            </div>
          )}
        </div>

        {/* Seção Pecuária */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <PiCowDuotone size={24} /> Pecuária
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <KpiCard
              title="GMD (Ganho Médio Diário)"
              value={formatarNumero(animatedValues.gmd, 3)}
              subtitle="kg/dia"
              icon={<VscGraph size={20} />}
              color="green"
              onClick={() => setSelectedKpi({ id: "gmd", title: "GMD — Ganho Médio Diário", value: `${formatarNumero(kpis.pecuaria.gmd, 3)} kg/dia`, section: "Pecuária", color: "green" })}
            />
            <KpiCard
              title="Taxa de Mortalidade"
              value={formatarPorcentagem(kpis.pecuaria.taxa_mortalidade)}
              icon={<ImWarning size={20} />}
              color="red"
              onClick={() => setSelectedKpi({ id: "taxa_mortalidade", title: "Taxa de Mortalidade", value: formatarPorcentagem(kpis.pecuaria.taxa_mortalidade), section: "Pecuária", color: "red" })}
            />
            <KpiCard
              title="Taxa de Prenhez"
              value={formatarPorcentagem(kpis.pecuaria.taxa_prenhez)}
              icon={<TbTargetArrow size={20} />}
              color="purple"
              onClick={() => setSelectedKpi({ id: "taxa_prenhez", title: "Taxa de Prenhez", value: formatarPorcentagem(kpis.pecuaria.taxa_prenhez), section: "Pecuária", color: "purple" })}
            />
            <KpiCard
              title="Lotação"
              value={formatarNumero(kpis.pecuaria.lotacao)}
              subtitle="UA/ha"
              icon={<PiCowDuotone size={20} />}
              color="teal"
              onClick={() => setSelectedKpi({ id: "lotacao", title: "Lotação do Rebanho", value: `${formatarNumero(kpis.pecuaria.lotacao)} UA/ha`, section: "Pecuária", color: "teal" })}
            />
          </div>

          {kpis.graficos?.gmd_por_lote?.length > 0 && (
            <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-700 mb-4">GMD por Lote</h3>
              <div className="h-80">
                <Bar data={gmdChartData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: "top" }, tooltip: { callbacks: { label: (ctx) => `${ctx.raw} kg/dia` } } } }} />
              </div>
            </div>
          )}
        </div>

        {/* Seção Financeiro */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <GrMoney size={24} /> Financeiro
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <KpiCard
              title="Lucro Líquido"
              value={formatarMoeda(animatedValues.lucro_liquido)}
              icon={<GrMoney size={20} />}
              color="green"
              onClick={() => setSelectedKpi({ id: "lucro_liquido", title: "Lucro Líquido", value: formatarMoeda(kpis.financeiro.lucro_liquido), section: "Financeiro", color: "green" })}
            />
            <KpiCard
              title="ROI"
              value={formatarPorcentagem(animatedValues.roi)}
              icon={<VscGraph size={20} />}
              color="blue"
              onClick={() => setSelectedKpi({ id: "roi", title: "ROI — Retorno sobre Investimento", value: formatarPorcentagem(kpis.financeiro.roi), section: "Financeiro", color: "blue" })}
            />
            <KpiCard
              title="Ponto de Equilíbrio"
              value={formatarMoeda(kpis.financeiro.ponto_equilibrio)}
              subtitle="/mês"
              icon={<FaScaleUnbalancedFlip size={20} />}
              color="orange"
              onClick={() => setSelectedKpi({ id: "ponto_equilibrio", title: "Ponto de Equilíbrio", value: formatarMoeda(kpis.financeiro.ponto_equilibrio), subtitle: "por mês", section: "Financeiro", color: "orange" })}
            />
          </div>

          {kpis.graficos?.evolucao_financeira?.length > 0 && (
            <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Evolução Financeira (Últimos 12 Meses)</h3>
              <div className="h-80">
                <Line data={evolucaoChartData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: "top" }, tooltip: { callbacks: { label: (ctx) => formatarMoeda(ctx.raw) } } } }} />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center text-xs text-gray-400 py-4 border-t border-gray-200">
          © 2025 Novalis Agro • Dashboard de KPIs • Última atualização:{" "}
          {new Date().toLocaleString()}
        </div>
      </div>

      {/* Modal de detalhes */}
      {selectedKpi && (
        <KpiModal
          kpi={selectedKpi}
          kpis={kpis}
          onClose={() => setSelectedKpi(null)}
          formatarMoeda={formatarMoeda}
          formatarNumero={formatarNumero}
          formatarPorcentagem={formatarPorcentagem}
        />
      )}
    </div>
  );
};

export default DashboardKPI;
